<template>
  <FormHeader :formStep="formStep" @onBackClick="onBack" />
  <FormContainer>
    <TransitionWrapper>
      <ToastMessage />

      <FormTitle>{{ $t('address.title') }}</FormTitle>

      <FormContent>
        <div :class="$style.wrapper">
          <div :class="$style.addressWrapper">
            <div :class="$style.streetWrapper">
              <StreetAutocomplete
                :streetName="formData.address.streetName"
                @onChange="onStreetNameChange"
                @onAutocomplete="onAutocompleteSelected"
              />
            </div>
            <HgInput
              v-model="v$.streetNumber.$model"
              name="streetNumber"
              autocomplete="address-line2"
              :label="$t('address.houseNumberLabel')"
              :updateModelOnInput="true"
              :maxlength="5"
              :class="$style.houseNumber"
              :isDirty="v$.streetNumber.$dirty"
              :isInvalid="v$.streetNumber.$invalid"
              :data-1p-ignore="true"
              :data-lpignore="true"
              :data-protonpass-ignore="true"
              :data-bwignore="true"
              @blur="onInputBlur"
              @keydown="onHouseNumberKeyDown"
            >
              <template #errorMessages>
                <div v-if="v$.streetNumber.required.$invalid">
                  {{ t('location.errorMessage.houseNumber.missing') }}
                </div>
              </template>
            </HgInput>
          </div>
          <Transition name="map-appear" appear>
            <StaticMap
              v-if="isValidLocation === true"
              v-scroll-into-view="!isIv24FunnelActive"
              :lon="(isValidLocation && formData.address.lon) || 0"
              :lat="(isValidLocation && formData.address.lat) || 0"
              :size="`${BREAKPOINTS.funnel_container_max_width}x160`"
              :resizeBreakpoint="BREAKPOINTS.funnel_container_max_width + 32"
              :class="$style.staticMap"
            />
          </Transition>
          <FormBottom v-if="isValidLocation" :onNext="onNext" />
          <FormBottom v-else :onNext="onNext" :nextButtonLabel="$t('location.nextButtonLabel')" />
        </div>
      </FormContent>
      <AlternativeAddressSuggestions
        v-if="isValidLocation === false"
        @onSelectAlternative="onSelectAlternative"
      />
    </TransitionWrapper>
  </FormContainer>
</template>

<script lang="ts" setup>
import {
  FormBottom,
  FormContainer,
  FormContent,
  FormHeader,
  FormTitle,
  ToastMessage,
  TransitionWrapper,
} from '@/components/common';
import AlternativeAddressSuggestions from '@/components/property-valuation-page/StreetAndNumber/components/AlternativeAddressSuggestions.vue';
import { formatAddress } from '@/components/property-valuation-page/StreetAndNumber/formatAddress';
import { useTrackingWithTreatment } from '@/composables/useTrackingWithTreatment';
import { usePropertyDetailsStore } from '@/stores/propertyDetails/propertyDetails';
import { usePropertyValuationStore } from '@/stores/propertyValuation';
import { Location } from '@/stores/types/propertyValuation';
import { getStepInputs } from '@/tracking/tracking';
import { BREAKPOINTS, NAVIGATION_KEYS, ToastType, ValuationFlow } from '@/types';
import { HgInput } from '@smg-real-estate/domus';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { storeToRefs } from 'pinia';
import { defineAsyncComponent, onBeforeUnmount, onMounted, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

interface AddressFormState {
  streetNumber: string;
}

const StaticMap = defineAsyncComponent(
  () => import('@/components/common/GoogleMaps/StaticMap.vue'),
);
const StreetAutocomplete = defineAsyncComponent(
  () => import('./components/StreetAutocomplete.vue'),
);

const { t, locale } = useI18n();
const { query } = useRoute();
const { trackVirtualPageViewWithTreatment } = useTrackingWithTreatment();
const propertyDetailsStore = usePropertyDetailsStore();
const propertyValuationStore = usePropertyValuationStore();
const {
  data: formData,
  step: formStep,
  isValidLocation,
  isIv24FunnelActive,
} = storeToRefs(propertyValuationStore);

const state = reactive<AddressFormState>({
  streetNumber: formData.value.address?.streetNumber ?? '',
});

const rules = {
  streetNumber: {
    required,
  },
};

const v$ = useVuelidate(rules, state);

watch(
  () => formData.value.address.streetNumber,
  (newStreetNumber: string | undefined) => {
    state.streetNumber = newStreetNumber ?? '';
    propertyValuationStore.dropLocationValidity();
  },
);

watch(isValidLocation, (newValue) => {
  if (newValue === true) {
    propertyDetailsStore.setToastType(ToastType.LocationSuccess);
  } else {
    propertyDetailsStore.setToastType(ToastType.None);
  }
});

onMounted(() => {
  trackVirtualPageViewWithTreatment({
    subType: 'input_location_street',
    locale: locale.value,
    stepInputs: getStepInputs(formData.value, (query.source as string) ?? '', formStep.value),
  });
});

onBeforeUnmount(() => {
  propertyValuationStore.dropLocationValidity();
});

const onStreetNameChange = ({ streetName }: { streetName: string }): void => {
  const isSameStreetName = streetName === formData.value.address.streetName;
  if (isSameStreetName) return;
  propertyValuationStore.dropLocationValidity();
  propertyValuationStore.setPartialLocation({ streetName });
};

const onAutocompleteSelected = async (location?: Location): Promise<void> => {
  propertyValuationStore.dropLocationValidity();
  if (location) {
    propertyValuationStore.setLocation(location);
  }

  // Extract and populate street number if available
  const { streetNumber } = location ?? {};
  if (streetNumber) {
    state.streetNumber = streetNumber;
    v$.value.streetNumber.$touch();
  }
};

const onInputBlur = () => {
  const { streetName } = formData.value.address;
  const streetNumber = state.streetNumber;

  propertyValuationStore.setPartialLocation({
    streetNumber,
    street: [streetName, streetNumber].filter(Boolean).join(' '),
  });

  v$.value.streetNumber.$touch();
};

const onHouseNumberKeyDown = (e: KeyboardEvent) => {
  const isAllowedKey = /^[a-zA-Z0-9]$/.test(e.key) || NAVIGATION_KEYS.includes(e.key);

  if (!isAllowedKey) {
    e.preventDefault();
  }
};

const onSelectAlternative = (location: Location) => {
  propertyValuationStore.dropLocationValidity();
  propertyValuationStore.setPartialLocation(location);
  onNext();
};

const onBack = (): void => {
  propertyValuationStore.navigateBack(ValuationFlow.StreetAndNumber);
};

const onNext = async (): Promise<void> => {
  if (isValidLocation.value === true) {
    propertyValuationStore.navigateNext(ValuationFlow.StreetAndNumber);
    return;
  }

  v$.value.$touch();
  if (v$.value.$invalid) return;

  const address = formatAddress(formData.value.address);

  const { gmapsApi } = await import('@/services/gmapsApi');
  const placePredictions = await gmapsApi.getPlacePredictionsWithPostalCode(address);
  const findPreciseResult = (placePredictions: Location[]) => {
    return placePredictions.find((place) => place.formattedAddress === address);
  };
  const result = findPreciseResult(placePredictions);
  if (result) {
    propertyValuationStore.setLocationValidity(true);
    propertyValuationStore.setLocation(result);
  } else {
    propertyValuationStore.setLocationValidity(false);
    propertyValuationStore.setLocationAlternatives(placePredictions);
  }
};
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

$house-number-width-large-screen: 144px;
$house-number-width-small-screen: 100px;

.wrapper {
  position: relative;
}

.addressWrapper {
  display: flex;
  gap: $space-100;

  @include grid-media-query(sm) {
    flex-direction: row;
    gap: $space-75;
    margin-bottom: 0;
  }
}

.streetWrapper {
  flex: 1;
}

.houseNumber {
  flex: 0 0 $house-number-width-small-screen;
  @include grid-media-query(sm) {
    flex: 0 0 $house-number-width-large-screen;
  }
}

.staticMap {
  margin-top: $space-100;
  padding-bottom: $space-200;
}

:global(.map-appear-enter-active) {
  transition: all 0.6s ease-in-out;
}

:global(.map-appear-enter-from) {
  opacity: 0;
  transform: translateY(20px);
}

:global(.map-appear-enter-to) {
  opacity: 1;
  transform: translateY(0);
}

:global(.map-appear-leave-active) {
  transition: all 0.6s ease-in-out;
}

:global(.map-appear-leave-from) {
  opacity: 1;
  transform: translateY(0);
}
</style>
